import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Stats, useInstantSearch } from 'react-instantsearch';
import cn from 'classnames';
import { CurrentRefinementsConnectorParamsItem } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import dynamic from 'next/dynamic';

import { SortResults } from '@components/algolia';
import Button from '@components/ui/Button';
import Heading from '@components/ui/Heading';
import {
  FEATURE_FLAG_PLP_2026_1,
  FEATURE_FLAG_PLP_FILTERS_V2,
  FEATURE_FLAG_PLP_STICKY_FILTERS,
  FEATURE_FLAG_PLP_FULL_WIDTH,
  FEATURE_FLAG_PLP_SORT_BAR_V2,
  PRODUCT_LINE_CAROUSEL_V2,
} from '@config/feature-flag';
import { colours } from '../Sidebar/ColourRefinement/utils';
import FilterBar from '../FilterBar';
import { useFilters } from '../FiltersProvider';
import StickyFilterBarRow from '../StickyFilterBarRow';

import CustomCurrentRefinements from './CurrentRefinements';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { AlgonomyPlacement } from '@components/global/Recommendations/Loader';
import { SkeletonCards } from '@components/global/Recommendations/SkeletonCards';
import StoreAvailabilityToggle from './StoreAvailabilityToggle';
import { ProductLineCarouselType } from '@adapters/prismic/types/plp';
import PlpSortBar from '_components/PlpSortBar';
import type { ProductStripDealsType } from '@adapters/prismic/types/redesignTypes';
import type { DealsGridData } from '_components/Products/DealsGrid/types';
import ProductStripDeals, {
  hasRenderableDeals,
} from '_components/Products/ProductStripDeals';
import CategoryNavigationTiles from '_components/CategoryNavigationTiles';
import type { CategoryNavigationTilesData } from '@adapters/prismic/transforms/categoryNavigationTiles';
import type { ImageProductStripData } from '@adapters/prismic/transforms/imageProductStrip';

import styles from './Header.module.scss';

const Recommendations = dynamic(
  () => import('@components/global/Recommendations'),
  {
    loading: () => <SkeletonCards count={5} />,
  }
);

const DealsGrid = dynamic(() => import('_components/Products/DealsGrid'));
const ImageProductStrip = dynamic(
  () => import('_components/ImageProductStrip')
);

/**
 * FilterBar renders nothing on a PLP without facets, so the row around it is
 * skipped too — otherwise its padding would pin under the site header as a
 * blank bar. Only mount under FEATURE_FLAG_PLP_FILTERS_V2, the only place a
 * FiltersProvider exists.
 */
const FilterBarRow = () => {
  const { pillFacets } = useFilters();
  const {
    indexUiState: {
      refinementList,
      hierarchicalMenu,
      menu,
      numericMenu,
      range,
      toggle,
    },
  } = useInstantSearch();
  const filtersKey = JSON.stringify([
    refinementList,
    hierarchicalMenu,
    menu,
    numericMenu,
    range,
    toggle,
  ]);

  if (pillFacets.length === 0) return null;

  return FEATURE_FLAG_PLP_STICKY_FILTERS ? (
    <StickyFilterBarRow className={styles.filterBarRow} resetKey={filtersKey}>
      <FilterBar />
    </StickyFilterBarRow>
  ) : (
    <div className={styles.filterBarRow}>
      <FilterBar />
    </div>
  );
};

const Header = ({
  locale,
  title,
  currentURL,
  openMobileNavigation,
  recommendations,
  heroTitle,
  productLineCarousel,
  productStripDeals,
  dealsGrid,
  dealsGridPriority = false,
  imageProductStrip = null,
  categoryNavigationAboveResults = null,
  categoryNavigationAfterDeals = null,
}: {
  locale?: string;
  title?: string;
  currentURL: string;
  openMobileNavigation: () => void;
  recommendations?: AlgonomyPlacement[];
  heroTitle?: string;
  productLineCarousel: ProductLineCarouselType | null;
  productStripDeals?: ProductStripDealsType[] | null;
  dealsGrid?: DealsGridData | null;
  dealsGridPriority?: boolean;
  imageProductStrip?: ImageProductStripData | null;
  /**
   * ED-218 Category Navigation Tiles that merch placed "above results":
   * rendered under the h1, before the deals grid and strips.
   */
  categoryNavigationAboveResults?: CategoryNavigationTilesData | null;
  /**
   * ED-218 Category Navigation Tiles that merch placed "above results, after
   * deals": rendered after the deals grid and strips, before recommendations.
   */
  categoryNavigationAfterDeals?: CategoryNavigationTilesData | null;
}) => {
  const { t } = useTranslation(['searchResults']);

  // `ProductStripDeals` renders nothing when a strip has no renderable
  // tiles, so filter those out here too — otherwise the wrapper below
  // survives as an empty box still carrying its rhythm margin.
  const renderableDealsStrips =
    productStripDeals?.filter((dealsStrip) =>
      hasRenderableDeals(dealsStrip.items, locale)
    ) ?? [];

  const [popularRecommendations] = recommendations || [null, null];
  const recommendationsRef = useRef<any>(null);
  const recommendationsRefValue = useObjectVisibility(recommendationsRef, 0.05);
  const [isRecommendationsRef, setIsRecommendationsRef] = useState(false);

  useEffect(() => {
    if (!isRecommendationsRef) setIsRecommendationsRef(recommendationsRefValue);
  }, [isRecommendationsRef, recommendationsRefValue]);

  const isSearchPage = currentURL.includes('/search');

  let excludedAttributes = ['categories.lvl0'];
  try {
    const isFilteringByCategory = Boolean(
      new URL(currentURL).searchParams.get('category')
    );

    excludedAttributes =
      isSearchPage && isFilteringByCategory ? [] : ['categories.lvl0'];
  } catch (error: any) {
    console.log('error excluding attributes from current refinements');
  }

  function excludeFacets(items: CurrentRefinementsConnectorParamsItem[]) {
    return items.filter((item) => item.attribute !== 'storeInventory');
  }

  const updateLabelsIfIndexIdContainsFr = useCallback(
    (items: CurrentRefinementsConnectorParamsItem[]) => {
      return items.map((item) => {
        if (item.indexId.includes('_fr')) {
          item.refinements = item.refinements.map((refinement) => {
            if (refinement.attribute === 'colour_variants') {
              const frenchLabel =
                colours[refinement.label]?.frenchSwatchName ?? refinement.label;
              refinement.label = frenchLabel;
            }
            return refinement;
          });
        }
        return item;
      });
    },
    []
  );

  // Shared by both the legacy grid and the redesigned `PlpSortBar` layout.
  const stats = (
    <Stats
      classNames={{
        root: styles.statsRoot,
      }}
      translations={{
        rootElementText({ nbHits, nbSortedHits, areHitsSorted }) {
          return areHitsSorted && nbHits !== nbSortedHits
            ? `${nbSortedHits!.toLocaleString(locale)} ${t(
                'search.results.num'
              )} ${nbHits.toLocaleString(locale)}`
            : `${t('search.sortby.showing')} ${nbHits.toLocaleString(
                locale
              )} ${t('search.results.title')}`;
        },
      }}
    />
  );

  // The redesigned filters (ED-320) replace this button with `FilterBar`'s "view
  // filters" trigger and stop rendering `Sidebar` altogether, so the legacy
  // button would open a drawer that is no longer mounted.
  const mobileFacetButton = FEATURE_FLAG_PLP_FILTERS_V2 ? null : (
    <Button
      className={styles.mobileFacetButton}
      handleClick={openMobileNavigation}
      variant="secondary"
      label={t('search.facets.button')}
      size="large"
      icon="filter"
    />
  );

  return (
    <div
      className={cn(styles.header, {
        [styles.headerSortBarV2]: FEATURE_FLAG_PLP_SORT_BAR_V2,
        [styles.headerStickyFilters]: FEATURE_FLAG_PLP_STICKY_FILTERS,
      })}
    >
      {!heroTitle &&
      title &&
      // The legacy carousel (components/algolia/ProductLineCarousel)
      // renders its own <h1> title, so this stays suppressed when it's in
      // use; the redesigned _components/ProductLineCarousel renders its
      // title as an <h2> instead, so the page still needs this fallback
      // — just screen-reader-only, since the carousel already shows the
      // same title visibly.
      (productLineCarousel === null || PRODUCT_LINE_CAROUSEL_V2) ? (
        <Heading
          semanticLevel="h1"
          styleLevel="1"
          className={cn({
            [styles.screenReaderOnly]: productLineCarousel !== null,
            [styles.titleV2]: FEATURE_FLAG_PLP_2026_1,
            // Only when the heading is actually visible: `screenReaderOnly`
            // takes the h1 out of flow, so a rhythm margin there would be
            // dead weight at best and fight `%sr-only`'s own margin at worst.
            [styles.titleRhythm]: productLineCarousel === null,
          })}
        >
          {title ?? t('search.allProducts')}
        </Heading>
      ) : null}

      {categoryNavigationAboveResults ? (
        <div className={styles.categoryNavigationSection}>
          <CategoryNavigationTiles
            interactionLocation="plp_top"
            heading={categoryNavigationAboveResults.heading}
            shopAll={categoryNavigationAboveResults.shopAll}
            items={categoryNavigationAboveResults.items}
            layout="carousel"
            contained
            // The slot directly under the h1 is in the first viewport.
            priority
          />
        </div>
      ) : null}

      {dealsGrid ? (
        <div className={styles.dealsGridSection}>
          <DealsGrid
            layout={dealsGrid.layout}
            cards={dealsGrid.cards}
            priority={dealsGridPriority}
          />
        </div>
      ) : null}

      {renderableDealsStrips.map((dealsStrip, index) => (
        // The wrapper, not the band itself, owns the gap below it: the band
        // is a full-bleed module whose spacing belongs to this stack, and
        // the same rhythm has to hold whether a hero, an h1, or nothing at
        // all sits above it.
        <div
          className={styles.dealsStripSection}
          key={dealsStrip.id ?? `deals-strip-${index}`}
        >
          <ProductStripDeals
            title={dealsStrip.title}
            items={dealsStrip.items}
            locale={locale}
          />
        </div>
      ))}

      {imageProductStrip ? (
        <div className={styles.imageProductStripSection}>
          <ImageProductStrip
            {...imageProductStrip}
            // Same rule as the tiles below: only the LCP candidate when
            // nothing image-led renders above it.
            priority={
              dealsGridPriority &&
              !dealsGrid &&
              renderableDealsStrips.length === 0
            }
          />
        </div>
      ) : null}

      {categoryNavigationAfterDeals ? (
        <div className={styles.categoryNavigationSection}>
          <CategoryNavigationTiles
            interactionLocation="plp_after_deals"
            heading={categoryNavigationAfterDeals.heading}
            shopAll={categoryNavigationAfterDeals.shopAll}
            items={categoryNavigationAfterDeals.items}
            layout="carousel"
            contained
            // `dealsGridPriority` already means nothing image-led sits above
            // this stack; the tiles only lead it when no deals render first.
            priority={
              dealsGridPriority &&
              !dealsGrid &&
              renderableDealsStrips.length === 0 &&
              !imageProductStrip
            }
          />
        </div>
      ) : null}

      {popularRecommendations ? (
        <div
          ref={recommendationsRef}
          className={cn(styles.headerRecommendations, {
            [styles.headerRecommendationsFullWidth]:
              FEATURE_FLAG_PLP_FULL_WIDTH,
          })}
        >
          <Recommendations
            isPlp
            placement={popularRecommendations}
            key={popularRecommendations.name}
          />
        </div>
      ) : null}

      {FEATURE_FLAG_PLP_SORT_BAR_V2 ? (
        <PlpSortBar
          locale={locale}
          stats={stats}
          filtersButton={mobileFacetButton}
        />
      ) : (
        <div className={styles.searchResultsUI}>
          <div className={styles.searchResultsStats}>{stats}</div>
          {mobileFacetButton}

          <div className={styles.searchResultsSort}>
            <SortResults locale={locale} />
          </div>
          <div className={styles.storeAvailability}>
            <StoreAvailabilityToggle />
          </div>
        </div>
      )}
      {FEATURE_FLAG_PLP_FILTERS_V2 && <FilterBarRow />}
      <CustomCurrentRefinements
        locale={locale}
        excludedAttributes={excludedAttributes}
        transformItems={(items) => {
          return updateLabelsIfIndexIdContainsFr(excludeFacets(items));
        }}
        classNames={{
          noRefinementRoot: styles.noRefinementRoot,
          list: styles.currentRefinementsList,
          item: styles.currentRefinementsItem,
          label: styles.currentRefinementsLabel,
          // QA (ED-210): the ED-44 Chip-token restyle only applies once the
          // redesigned filter system is active — this same chip renders on
          // any live PLP page today, flag or no flag, so the legacy classes
          // stay the default.
          category: FEATURE_FLAG_PLP_FILTERS_V2
            ? styles.currentRefinementsCategoryV2
            : styles.currentRefinementsCategory,
          delete: FEATURE_FLAG_PLP_FILTERS_V2
            ? styles.currentRefinementsDeleteV2
            : styles.currentRefinementsDelete,
        }}
      />
    </div>
  );
};

export default Header;
